import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import TeamPortraitCard from '../TeamPortraitCard/TeamPortraitCard';
import FixedTeamBanner from '../FixedTeamBanner/FixedTeamBanner';
import TeamFantasyFitnessScoreCard from '../../ScoreCards/TeamFantasyFitnessScoreCard/TeamFantasyFitnessScoreCard';
import TeamMetricsCard from '../TeamMetricsCard/TeamMetricsCard';
import CoachesCard from '../CoachesCard/CoachesCard';
import TeamPlayersStatsTable from '../TeamPlayersStatsTable/TeamPlayersStatsTable';
import TeamMatchupsTable from '../TeamMatchupsTable/TeamMatchupsTable';
import FilterToggle from '../../Filters/FilterToggle';
import BackToTopButton from '../../Buttons/BackToTopButton/BackToTopButton';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

export default function TeamProfileCard(props) {
	const { id: paramId } = useParams();
	const id = (props.team && props.team.id) || paramId;
	const [
		team,
		setTeam
	] = useState(null);
	const [
		players,
		setPlayers
	] = useState([]);
	const rankModalRef = useRef(null);
	const currentTeamRef = useRef(null);
	const [
		gradeRanks,
		setGradeRanks
	] = useState([]);
	const [
		visibleRankModal,
		setVisibleRankModal
	] = useState(null);
	const [
		metrics,
		setMetrics
	] = useState([]);
	const [
		matchupsBySeason,
		setMatchupsBySeason
	] = useState({});
	const matchupSeasons = [
		2026,
		2025
	];
	const [
		selectedMatchupSeason,
		setSelectedMatchupSeason
	] = useState(matchupSeasons[0]);
	const [
		showBackToTop,
		setShowBackToTop
	] = useState(false);
	const [
		temperature,
		setTemperature
	] = useState(null);
	const [
		isHeatmapEnabled,
		setIsHeatmapEnabled
	] = useState(false);
	const [
		isHeatmapAgeCategoryEnabled,
		setIsHeatmapAgeCategoryEnabled
	] = useState(true);
	const [
		isHeatmap40TimeCategoryEnabled,
		setIsHeatmap40TimeCategoryEnabled
	] = useState(true);
	const [
		isHeatmapFFScoresCategoryEnabled,
		setIsHeatmapFFScoresCategoryEnabled
	] = useState(true);
	const [
		loading,
		setLoading
	] = useState(false);

	const handleHeatmapToggle = () => {
		setIsHeatmapEnabled((prevHeatmapState) => !prevHeatmapState);
	};

	const handleOptionChange = (key) => {
		switch (key) {
			case 'age':
				setIsHeatmapAgeCategoryEnabled(!isHeatmapAgeCategoryEnabled);
				break;
			case 'time':
				setIsHeatmap40TimeCategoryEnabled(!isHeatmap40TimeCategoryEnabled);
				break;
			case 'scores':
				setIsHeatmapFFScoresCategoryEnabled(!isHeatmapFFScoresCategoryEnabled);
				break;
			default:
				break;
		}
	};

	useEffect(
		() => {
			const allOptionsDisabled =
				!isHeatmapAgeCategoryEnabled &&
				!isHeatmap40TimeCategoryEnabled &&
				!isHeatmapFFScoresCategoryEnabled;
			if (allOptionsDisabled && isHeatmapEnabled) {
				setIsHeatmapEnabled(false);
				setIsHeatmapAgeCategoryEnabled(true);
				setIsHeatmap40TimeCategoryEnabled(true);
				setIsHeatmapFFScoresCategoryEnabled(true);
			}
		},
		[
			isHeatmapAgeCategoryEnabled,
			isHeatmap40TimeCategoryEnabled,
			isHeatmapFFScoresCategoryEnabled
		]
	);

	const heatmapOptions = [
		{ key: 'age', label: 'Age', value: isHeatmapAgeCategoryEnabled },
		{ key: 'time', label: '40 Time', value: isHeatmap40TimeCategoryEnabled },
		{
			key: 'scores',
			label: 'FF Scores',
			value: isHeatmapFFScoresCategoryEnabled
		}
	];

	const fetchTeamData = () => {
		setLoading(true);

		fetch(props.get_team_endpoint.replace('id', id))
			.then((response) => response.json())
			.then((result) => {
				setTeam(result.data.team);
				setPlayers(result.data.players);
				setMatchupsBySeason(result.data.matchups || {});
				setMetrics(result.data.metrics);
				setLoading(false);
			})
			.catch((error) => {
				console.error('Error fetching team data:', error);
				setLoading(false);
			});
	};

	useEffect(
		() => {
			window.scrollTo(0, 0);

			fetchTeamData();
		},
		[
			props.get_team_endpoint,
			id
		]
	);

	// Handle rank modal click
	const handleRankClick = async (gradeType) => {
		if (visibleRankModal === gradeType) {
			setVisibleRankModal(null);
		} else {
			setLoading(true);
			setVisibleRankModal(gradeType);

			try {
				const response = await fetch(`/api/v1/teams/ranks`);
				if (!response.ok) throw new Error('Network response was not ok');
				const data = await response.json();
				setLoading(false);
				setGradeRanks(data);
			} catch (error) {
				console.error('Failed to fetch grade ranks:', error);
			}
		}
	};

	const handleTeamClick = (teamId) => {
		setVisibleRankModal(null);
		window.location.href = `/teams/${teamId}`;
	};

	// Scroll to the selected team in the modal
	useEffect(
		() => {
			setTimeout(() => {
				if (visibleRankModal && currentTeamRef.current) {
					currentTeamRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'center'
					});
				}
			}, 500);
		},
		[
			visibleRankModal,
			currentTeamRef
		]
	);

	// Handle clicks outside the rank modal to close it
	const handleClickOutside = (event) => {
		if (rankModalRef.current && !rankModalRef.current.contains(event.target)) {
			setVisibleRankModal(null);
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const fetchTemperature = (location) => {
		let city = location.split(',')[0].trim();
		if (city === 'Tampa Bay') {
			city = 'Tampa';
		}
		fetch(
			`https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${'336d6d9ed7689fcfacbc7a9cd10f4250'}`
		)
			.then((response) => response.json())
			.then((data) => {
				if (
					data.main &&
					data.main.temp &&
					data.weather &&
					data.weather.length > 0
				) {
					// Convert temperature to Fahrenheit
					const tempInFahrenheit = Math.round(
						(data.main.temp - 273.15) * 9 / 5 + 32
					);
					// Get weather description
					const weatherDescription = data.weather[0].main;
					setTemperature({
						temp: tempInFahrenheit,
						weather: weatherDescription
					});
				}
			})
			.catch((error) => {
				console.error('Error fetching temperature:', error);
			});
	};

	useEffect(
		() => {
			if (team && team.location) {
				fetchTemperature(team.location);
			}
		},
		[
			team && team.location
		]
	);

	const [
		scrolledPastBanner,
		setScrolledPastBanner
	] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			const bannerHeight = document.querySelector('.banner-section')
				.offsetHeight;
			const scrolled = window.scrollY > bannerHeight;
			setScrolledPastBanner(scrolled);
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	// Handle scroll event to show "Back to Top" button
	useEffect(() => {
		const handleScroll = () => {
			if (window.scrollY > 420) {
				setShowBackToTop(true);
			} else {
				setShowBackToTop(false);
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	if (!team) {
		return (
			<div>
				<LoadingSpinner />
			</div>
		);
	}

	return (
		<div>
			{/* Team Portrait Card */}
			<div className="banner-section">
				<TeamPortraitCard
					loading={loading}
					team={team}
					temperature={temperature}
					get_team_endpoint={props.get_team_endpoint}
					get_teams_endpoint={props.get_teams_endpoint}
					get_team_ai_insights_endpoint={props.get_team_ai_insights_endpoint}
					create_checkout_session_endpoint={
						props.create_checkout_session_endpoint
					}
					stripe_public_key={props.stripe_public_key}
					stripe_product_essential_id={props.stripe_product_essential_id}
					stripe_product_advanced_id={props.stripe_product_advanced_id}
					stripe_product_ultimate_id={props.stripe_product_ultimate_id}
				/>
			</div>

			{/* Fixed Team Banner  - Displays after scrolling past the Team Portrait Card Section */}
			{scrolledPastBanner && (
				<FixedTeamBanner
					scrolledPastBanner={scrolledPastBanner}
					team={team}
					temperature={temperature}
				/>
			)}

			{/* Fantasy Fitness Scores Card */}
			<div className="text-3xl font-bold mt-14 mb-3 text-gray-800 dark:text-dark-text">
				<h4>Fantasy Fitness™ Scores</h4>
			</div>
			<TeamFantasyFitnessScoreCard
				loading={loading}
				team={team}
				gradeRanks={gradeRanks}
				handleRankClick={handleRankClick}
				visibleRankModal={visibleRankModal}
				handleTeamClick={handleTeamClick}
				rankModalRef={rankModalRef}
				currentTeamRef={currentTeamRef}
			/>

			{/* Team Metrics Card */}
			<TeamMetricsCard team={team} metrics={metrics} />

			{/* Players Card */}
			<div className="text-3xl font-bold mb-3 mt-14 text-gray-800 dark:text-dark-text">
				<h4>Players</h4>
			</div>
			<div className="relative hidden md:block">
				{/* Filter components */}
				<div
					style={{
						position: 'absolute',
						top: -58,
						right: -12
					}}
				>
					<FilterToggle
						label="Heatmap"
						icon={<i className="fa-solid fa-map" />}
						isChecked={isHeatmapEnabled}
						onToggle={handleHeatmapToggle}
						options={heatmapOptions}
						onOptionChange={handleOptionChange}
					/>
				</div>
			</div>
			<TeamPlayersStatsTable
				players={players}
				isHeatmapEnabled={isHeatmapEnabled}
				isHeatmapAgeCategoryEnabled={isHeatmapAgeCategoryEnabled}
				isHeatmap40TimeCategoryEnabled={isHeatmap40TimeCategoryEnabled}
				isHeatmapFFScoresCategoryEnabled={isHeatmapFFScoresCategoryEnabled}
			/>

			{/* Matchups Card */}
			{matchupSeasons.some(
				(season) =>
					(matchupsBySeason[season] || []).length > 0
			) && (
				<div className="mt-14">
					<div className="text-3xl font-bold mb-3 text-gray-800 dark:text-dark-text">
						<h4>Matchups</h4>
					</div>
					<div
						className="flex gap-6 border-b border-gray-300 dark:border-dark-border"
						role="tablist"
						aria-label="Matchups season"
					>
						{matchupSeasons.map((season) => {
							const isActive = selectedMatchupSeason === season;
							return (
								<button
									key={season}
									type="button"
									role="tab"
									aria-selected={isActive}
									onClick={() => setSelectedMatchupSeason(season)}
									className={`pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${isActive
										? 'border-blue-500 text-blue-600 dark:text-blue-400'
										: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
								>
									{season}
								</button>
							);
						})}
					</div>
					<TeamMatchupsTable
						key={selectedMatchupSeason}
						matchups={matchupsBySeason[selectedMatchupSeason] || []}
						team={team}
						metrics={metrics}
					/>
				</div>
			)}

			{/* Coaches Card */}
			<div className="text-3xl font-bold mb-3 mt-14 text-gray-800 dark:text-dark-text">
				<h4>Coaches</h4>
			</div>
			<CoachesCard team={team} />

			{/* Back to Top Widget */}
			{showBackToTop && <BackToTopButton />}
		</div>
	);
}
